import { Injectable, type Signal, signal } from '@angular/core';

/**
 * Whether the signed-in Clarity user is a Clarity SYS admin — **a rendering hint, never a boundary.**
 *
 * **Why root, when the session itself is not.** `ClaritySessionProvider` lives in the Clarity route
 * injector and dies with that subtree, by design. The platform shell's user menu sits above that
 * injector and still has to decide whether to show the Clarity admin entry, so the one bit it needs
 * is published here, where both sides can reach it. Nothing else crosses: no id, no email, no role
 * token — only the boolean.
 *
 * **Who writes it.** Only the session implementation, from the role Clarity reports for the caller
 * on every `restore()`, and cleared whenever the session is cleared. Until a Clarity session has been
 * restored in this tab it reads `false`, so the entry appears once the reader has opened any Clarity
 * screen — the fail-closed direction.
 *
 * **What it protects: nothing.** Brain refuses every admin endpoint to a caller without its own
 * `ClarityAdmin` permission; a patched bundle flipping this bit sees an admin page that cannot load.
 */
@Injectable({ providedIn: 'root' })
export class ClarityAdminRoleHint {
	private readonly admin = signal(false);

	public readonly isAdmin: Signal<boolean> = this.admin.asReadonly();

	/** Records what the session reported. Called by the session implementation only. */
	public set(isAdmin: boolean): void {
		this.admin.set(isAdmin);
	}
}
